<style scoped>
main {
	height: 100%;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}
.mc-photo {
	width: 100%;
	max-width: 280px;
	height: 160px;
	object-fit: cover;
	border-radius: 0.5rem;
}
.mc-photo-wrap .mc-photo {
	width: 160px;
}
/* Photos saved in an earlier session have no local preview (RMO has no public
   image route), so a placeholder tile stands in for the thumbnail. */
.mc-photo-saved {
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #e9f2fb;
	color: #0d6efd;
}
.mc-photo-remove {
	position: absolute;
	top: -0.4rem;
	right: -0.4rem;
	padding: 0 0.2rem;
	line-height: 1.1;
	border-radius: 50%;
}
.block-icon {
	width: 32px;
	height: 32px;
	border-radius: 0.5rem;
	background-color: #e9f2fb;
	color: #0d6efd;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}
.beyond-scroll {
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
}
</style>
<template>
	<main>
		<h2 class="h5 mb-2">{{ t("beyond_title") }}</h2>

		<div class="alert alert-light border mb-2 py-2">
			<p class="mb-0 small">
				{{ t("beyond_body", { district_name: org.name }) }}
			</p>
		</div>

		<div class="beyond-scroll">
			<div v-for="cat in categories" :key="cat" class="card mb-2">
				<div class="card-body py-2">
					<div
						class="d-flex align-items-center justify-content-between flex-wrap gap-2"
					>
						<h3 class="h6 mb-0 d-flex align-items-center gap-2">
							<span class="block-icon"><i :class="catIcon(cat)"></i></span>
							{{ blockLabel(cat) }}
						</h3>
						<div class="form-check form-switch mb-0">
							<input
								:id="'report-' + cat"
								class="form-check-input"
								type="checkbox"
								role="switch"
								:checked="enabled[cat]"
								@change="toggle(cat, $event)"
							/>
							<label class="form-check-label" :for="'report-' + cat">
								{{ t("yes_report") }}
							</label>
						</div>
					</div>

					<template v-if="enabled[cat]">
						<textarea
							class="form-control mt-2"
							:value="descriptions[cat]"
							:placeholder="t('describe_location')"
							rows="2"
							@input="onDescription(cat, $event)"
						></textarea>
						<div class="mt-2">
							<div v-if="photosFor(cat).length > 0" class="mb-2 d-flex gap-2 flex-wrap">
								<div
									v-for="photo in photosFor(cat)"
									:key="photo.id"
									class="mc-photo-wrap position-relative"
								>
									<img
										v-if="store.previews.get(photo.id)"
										:src="store.previews.get(photo.id)"
										class="mc-photo"
										:alt="t('photo_alt')"
									/>
									<span v-else class="mc-photo mc-photo-saved" :title="t('photo_saved')">
										<i class="bi bi-image fs-3"></i>
									</span>
									<button
										type="button"
										class="btn btn-sm btn-danger mc-photo-remove"
										:aria-label="t('remove_photo')"
										:title="t('remove_photo')"
										@click="removePhoto(cat, photo.id)"
									>
										<i class="bi bi-x"></i>
									</button>
								</div>
							</div>
							<input
								ref="fileInput"
								type="file"
								accept="image/*"
								capture="environment"
								multiple
								class="d-none"
								@change="onFile"
							/>
							<button
								type="button"
								class="btn btn-sm btn-outline-primary"
								@click="pickFile(cat)"
							>
								<i class="bi bi-camera me-1"></i>{{ t("add_photo") }}
							</button>
						</div>
					</template>
				</div>
			</div>
		</div>

		<div class="d-flex gap-2 mt-2">
			<button type="button" class="btn btn-outline-secondary" @click="goWalk">
				{{ t("back") }}
			</button>
			<button type="button" class="btn btn-primary" @click="goContact">
				{{ t("continue") }}
			</button>
		</div>
	</main>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref } from "vue";

import { type CopyKey, useOrg, useT } from "@/rmo/content/mosquito/copy";
import { router } from "@/rmo/route/config";
import { useRoutes } from "@/rmo/route/use";
import { useStoreVirtualInspection } from "@/rmo/store/virtualinspection";
import type { OffPropertyCategory } from "@/type/api";

const CATEGORIES: OffPropertyCategory[] = [
	"neighbor",
	"public",
	"agricultural",
];
const CATEGORY_LABELS: Record<OffPropertyCategory, CopyKey> = {
	neighbor: "block_neighbor",
	public: "block_public",
	agricultural: "block_agricultural",
};
const CATEGORY_ICONS: Record<OffPropertyCategory, string> = {
	neighbor: "bi-house",
	public: "bi-buildings",
	agricultural: "bi-flower3",
};

interface Props {
	token: string;
}

const props = defineProps<Props>();
const routes = useRoutes();
const store = useStoreVirtualInspection();
const t = useT();
const org = computed(useOrg);
const fileInput = ref<HTMLInputElement | null>(null);
const pendingCategory = ref<OffPropertyCategory | null>(null);

const categories = CATEGORIES;

// Local working state per category, initialized from the saved rows.
const enabled = reactive<Record<OffPropertyCategory, boolean>>({
	neighbor: false,
	public: false,
	agricultural: false,
});
const descriptions = reactive<Record<OffPropertyCategory, string>>({
	neighbor: "",
	public: "",
	agricultural: "",
});

const debounceTimers: Partial<Record<OffPropertyCategory, number>> = {};

function rowFor(cat: OffPropertyCategory) {
	return store.state?.off_property.find((o) => o.category === cat);
}

function photosFor(cat: OffPropertyCategory) {
	return rowFor(cat)?.photos ?? [];
}

// A report counts as "on" when a row exists with real content. Toggling off
// deletes the whole block (description + photos).
function initFromState() {
	for (const cat of CATEGORIES) {
		const row = rowFor(cat);
		enabled[cat] = Boolean(
			row && (row.description?.trim() || row.photos.length > 0),
		);
		descriptions[cat] = row?.description ?? "";
	}
}
initFromState();

function blockLabel(cat: OffPropertyCategory): string {
	return t(CATEGORY_LABELS[cat]);
}

function catIcon(cat: OffPropertyCategory): string {
	return CATEGORY_ICONS[cat];
}

function toggle(cat: OffPropertyCategory, e: Event) {
	const on = (e.target as HTMLInputElement).checked;
	enabled[cat] = on;
	if (!on) {
		// Turn off: delete the whole block (description + photos) so nothing
		// remains reported. Cancel any pending description save first.
		if (debounceTimers[cat]) {
			window.clearTimeout(debounceTimers[cat]);
			debounceTimers[cat] = undefined;
		}
		descriptions[cat] = "";
		for (const photo of photosFor(cat)) {
			store.clearPreview(photo.id);
		}
		if (rowFor(cat)) {
			store.deleteOffProperty(props.token, cat);
		}
	}
}

// One photo goes; the report and its other photos stay.
function removePhoto(cat: OffPropertyCategory, photoId: string) {
	store.removeOffPropertyPhoto(props.token, cat, photoId);
}

function onDescription(cat: OffPropertyCategory, e: Event) {
	descriptions[cat] = (e.target as HTMLTextAreaElement).value;
	schedule(cat);
}

function schedule(cat: OffPropertyCategory) {
	if (debounceTimers[cat]) {
		window.clearTimeout(debounceTimers[cat]);
	}
	debounceTimers[cat] = window.setTimeout(() => {
		debounceTimers[cat] = undefined;
		flush(cat);
	}, 600);
}

function flush(cat: OffPropertyCategory) {
	if (debounceTimers[cat]) {
		window.clearTimeout(debounceTimers[cat]);
		debounceTimers[cat] = undefined;
	}
	if (!enabled[cat]) {
		return; // block is off (deleted) — nothing to persist
	}
	const row = rowFor(cat);
	if (!row && !descriptions[cat].trim()) {
		return; // nothing worth reporting yet — avoid junk rows
	}
	store.saveOffProperty(props.token, {
		category: cat,
		description: descriptions[cat],
	});
}

function pickFile(cat: OffPropertyCategory) {
	pendingCategory.value = cat;
	fileInput.value?.click();
}

async function onFile(e: Event) {
	const input = e.target as HTMLInputElement;
	const files = [...(input.files ?? [])];
	const cat = pendingCategory.value;
	if (files.length === 0 || !cat) {
		return;
	}
	await store.uploadOffPropertyPhoto(props.token, cat, files);
	input.value = "";
}

function goWalk() {
	for (const cat of CATEGORIES) {
		flush(cat);
	}
	router.push(routes.MosquitoCheckWalk(props.token));
}

function goContact() {
	for (const cat of CATEGORIES) {
		flush(cat);
	}
	router.push(routes.MosquitoCheckContact(props.token));
}

onBeforeUnmount(() => {
	for (const cat of CATEGORIES) {
		flush(cat);
	}
});
</script>
